import { useDeferredValue, useEffect, useMemo, useState, type ReactNode } from 'react';
import { IconInbox, IconPencil, IconPlus, IconSearchX, IconTrash2, IconTriangleAlert, IconX } from '../icons';
import { CODE_MAX, matches, tn } from '../i18n';
import { describeError, useTranslate } from '../i18n/react';
import { Badge } from '../ui/Badge';
import { Button, IconButton } from '../ui/Button';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { EmptyState } from '../ui/EmptyState';
import { Alert, SearchInput, Select, Textarea, TextInput, Toggle } from '../ui/Field';
import { Card, PageHeader, Toolbar } from '../ui/Page';
import { RowMenu } from '../ui/RowMenu';
import { Table, TableFooter, type Column } from '../ui/Table';

/**
 * One definition screen, reused by all of them.
 *
 * The analysis of the system this replaces makes the case: its parameter screens are nearly
 * identical to one another and were clearly built one at a time, which is why they disagree
 * about small things — where the delete button is, whether a code is upper-cased, what
 * happens when a name is already taken. Written once, they cannot disagree.
 *
 * Rows are edited in place rather than in a dialog. These are two- and three-field records
 * and a dialog for a city name is a dialog nobody needs; the location card, which has
 * fifteen fields, gets a real form of its own.
 *
 * <p>Three things changed when the design system arrived, and all three were defects rather
 * than taste. Delete was a bin icon sitting under the cursor on every row, one mis-click from
 * taking a category away from its questions — it is now in the kebab menu behind a
 * confirmation that says what the delete costs. The action buttons were bare emoji with a
 * `title` and no accessible name. And "loading" and "there is nothing here" were the same
 * grey sentence, so a slow request looked like an empty table.</p>
 */

export type FieldKind = 'text' | 'textarea' | 'select' | 'toggle' | 'date';

export interface Field<D> {
  key: keyof D & string;
  label: string;
  kind?: FieldKind;
  options?: { value: string; label: string }[];
  placeholder?: string;
  /** Narrow columns — a code, a toggle — stay narrow instead of sharing the width evenly. */
  tight?: boolean;
  maxLength?: number;
  /** Applied as it is typed. Codes are upper-cased, because they are read out loud in stores. */
  transform?: (value: string) => string;
}

export interface ReadOnlyColumn<R> {
  label: string;
  render: (row: R) => ReactNode;
}

interface Props<R extends { id: string }, D> {
  title: string;
  subtitle?: string;
  crumbs?: string[];
  fields: Field<D>[];
  columns?: ReadOnlyColumn<R>[];
  rows: R[];
  loading: boolean;
  blank: D;
  toDraft: (row: R) => D;
  cell: (row: R, key: keyof D & string) => ReactNode;
  save: (draft: D, id: string | null) => Promise<void>;
  remove: (row: R) => Promise<void>;
  empty: string;
  /** What this screen is for, under the title. */
  emptyText?: string;
  /**
   * What the delete confirmation asks about, and what it would cost.
   *
   * <p>Required, because the alternative was a dialog headed "Ülkeler silinsin mi?" — the
   * name of the screen, not of the row — with nothing about the nine locations that would
   * be left pointing at nothing. Saying what a delete costs is the one thing the dialog is
   * for, so it cannot be the thing a screen forgets to pass.</p>
   */
  describe: (row: R) => { name: string; warning?: string };
  /** Searched against. Without it the toolbar has no search box. */
  searchable?: (row: R) => string;
  searchPlaceholder?: string;
  /** Set when the screen cannot be added to yet — no country picked, say. */
  blocked?: string;
  children?: ReactNode;
}

export function DefinitionTable<R extends { id: string }, D>({
  title,
  subtitle,
  crumbs,
  fields,
  columns = [],
  rows,
  loading,
  blank,
  toDraft,
  cell,
  save,
  remove,
  empty,
  emptyText,
  describe,
  searchable,
  searchPlaceholder,
  blocked,
  children,
}: Props<R, D>) {
  const t = useTranslate();
  const [draft, setDraft] = useState<{ id: string | null; values: D } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [doomed, setDoomed] = useState<R | null>(null);
  const [term, setTerm] = useState('');

  // Filtering several hundred rows in the client; deferring keeps the keystrokes smooth.
  const deferred = useDeferredValue(term);
  const matching = useMemo(
    () => (searchable && deferred.trim() ? rows.filter((row) => matches(searchable(row), deferred)) : rows),
    [rows, deferred, searchable],
  );

  // Paged, because Districts drew all 973 rows at once — some 16,600 DOM nodes on every
  // keystroke in the search box (field test, 2 November). Short lists never see the footer.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  useEffect(() => setPage(1), [deferred, pageSize, rows]);
  const shown = matching.slice((page - 1) * pageSize, page * pageSize);

  async function commit() {
    if (!draft || busy) return;

    setBusy(true);
    try {
      await save(draft.values, draft.id);
      // A new row lands wherever the sort puts it, usually pages away; the new district was
      // saved and seemed lost (field test, 3 November). Searching for it shows it at once.
      const name = (draft.values as Record<string, unknown>).name;
      if (draft.id === null && searchable && typeof name === 'string' && name.trim()) setTerm(name.trim());
      setDraft(null);
      setError(null);
    } catch (e) {
      // The server has sent the code that says what is wrong; the client holds the sentence,
      // in whichever language this build is showing.
      setError(describeError(e, 'error.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function drop(row: R) {
    setBusy(true);
    try {
      await remove(row);
      setError(null);
    } catch (e) {
      setError(describeError(e, 'error.deleteFailed'));
    } finally {
      setBusy(false);
    }
  }

  const input = (field: Field<D>, values: D) => {
    const set = (value: unknown) => setDraft({ ...draft!, values: { ...values, [field.key]: value } });
    const value = values[field.key];

    if (field.kind === 'toggle') {
      return (
        <Toggle checked={Boolean(value)} onCheckedChange={set} label={<span className="nv-sr-only">{field.label}</span>} />
      );
    }

    if (field.kind === 'select') {
      return (
        <Select aria-label={field.label} value={String(value ?? '')} onChange={(e) => set(e.target.value || null)}>
          <option value="">{t('common.none')}</option>
          {field.options?.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </Select>
      );
    }

    if (field.kind === 'date') {
      return (
        <TextInput
          type="date"
          aria-label={field.label}
          value={String(value ?? '')}
          onChange={(e) => set(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void commit();
            if (e.key === 'Escape') setDraft(null);
          }}
        />
      );
    }

    if (field.kind === 'textarea') {
      return (
        <Textarea
          aria-label={field.label}
          placeholder={field.placeholder ?? t('common.optional')}
          value={String(value ?? '')}
          onChange={(e) => set(e.target.value)}
        />
      );
    }

    return (
      <TextInput
        aria-label={field.label}
        placeholder={field.placeholder}
        maxLength={field.maxLength ?? (field.key === 'code' ? CODE_MAX : undefined)}
        value={String(value ?? '')}
        onChange={(e) => set(field.transform ? field.transform(e.target.value) : e.target.value)}
        // Enter saves. These are one-line records, and reaching for the mouse to confirm a
        // city name is the difference between typing eighty of them and giving up.
        onKeyDown={(e) => {
          if (e.key === 'Enter') void commit();
          if (e.key === 'Escape') setDraft(null);
        }}
      />
    );
  };

  const editingRow = (values: D) => (
    <tr className="is-draft">
      {fields.map((field) => (
        <td key={field.key} data-label={field.label}>{input(field, values)}</td>
      ))}
      {columns.map((column) => (
        <td key={column.label} data-label={column.label} />
      ))}
      <td className="nv-actions-col">
        <div className="nv-row">
          <Button variant="primary" size="sm" disabled={busy} loading={busy} loadingText={t('common.saving')} onClick={() => void commit()}>
            {t('common.save')}
          </Button>
          <IconButton label={t('common.cancel')} icon={IconX} onClick={() => setDraft(null)} />
        </div>
      </td>
    </tr>
  );

  const tableColumns: Column<R>[] = [
    ...fields.map(
      (field, i): Column<R> => ({
        id: field.key,
        header: field.label,
        cell: (row) => (draft?.id === row.id ? input(field, draft.values) : cell(row, field.key)),
        // A tight column is sized to what it shows — a code like "VM", two characters —
        // which is right while reading and wrong while editing. The input that replaces
        // the text is `width: 100%` of its column, so a column sized to two characters
        // leaves the control nothing but its own padding: a box that takes focus and
        // accepts keystrokes with nowhere to show them.
        //
        // A length rather than a percentage, because the two are honoured differently. A
        // percentage is a share of the table and can be squeezed; a length is a floor the
        // column keeps whatever else is competing for the width. 11rem is what the column
        // already works out to when nothing squeezes it, so nothing looks different where
        // it is fine today — it simply cannot collapse below that any more.
        //
        // Only while a row is being edited. A code column with nothing but codes in it
        // should stay as narrow as the codes; widening it permanently would take the room
        // from the name and the description for the sake of a box that is not there.
        //
        // A toggle is not typed into and keeps the percentage either way.
        width: field.tight ? (field.kind !== 'toggle' && draft ? '11rem' : '1%') : undefined,
        className: i === 0 ? 'is-primary' : undefined,
        skeletonWidth: field.tight ? '48px' : i === 0 ? '62%' : '40%',
      }),
    ),
    ...columns.map(
      (column): Column<R> => ({
        id: column.label,
        header: column.label,
        cell: (row) => (draft?.id === row.id ? null : column.render(row)),
        className: 'is-muted',
        skeletonWidth: '56px',
      }),
    ),
  ];

  const filtered = Boolean(searchable && deferred.trim());

  return (
    <>
      <PageHeader
        crumbs={crumbs}
        title={title}
        subtitle={subtitle}
        action={
          <Button
            variant="primary"
            icon={IconPlus}
            disabled={Boolean(blocked)}
            title={blocked}
            onClick={() => setDraft({ id: null, values: blank })}
          >
            {t('common.add')}
          </Button>
        }
      />

      {error ? <Alert>{error}</Alert> : null}
      {blocked ? <Alert tone="warning">{blocked}</Alert> : null}

      <Card className="nv-table-card">
        {(searchable || children) && (
          <Toolbar count={rows.length > 0 ? tn('common.shown', matching.length) : undefined}>
            {searchable ? (
              <SearchInput
                value={term}
                onValueChange={setTerm}
                placeholder={searchPlaceholder ?? t('common.search')}
              />
            ) : null}
            {children}
            {filtered ? (
              <Button variant="ghost" size="sm" onClick={() => setTerm('')}>{t('common.clearFilters')}</Button>
            ) : null}
          </Toolbar>
        )}

        <Table
          caption={title}
          columns={tableColumns}
          rows={shown}
          getRowId={(row) => row.id}
          status={loading ? 'loading' : 'ready'}
          leadingRow={draft?.id === null ? editingRow(draft.values) : undefined}
          rowClassName={(row) => (draft?.id === row.id ? 'is-draft' : undefined)}
          rowActions={(row) =>
            draft?.id === row.id ? (
              <div className="nv-row">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={busy}
                  loading={busy}
                  loadingText={t('common.saving')}
                  onClick={() => void commit()}
                >
                  {t('common.save')}
                </Button>
                <IconButton label={t('common.cancel')} icon={IconX} onClick={() => setDraft(null)} />
              </div>
            ) : (
              <>
                <IconButton
                  label={t('common.edit')}
                  icon={IconPencil}
                  onClick={() => setDraft({ id: row.id, values: toDraft(row) })}
                />
                <RowMenu
                  label={t('common.moreActions')}
                  items={[
                    {
                      label: t('common.delete'),
                      icon: IconTrash2,
                      danger: true,
                      disabled: busy,
                      onSelect: () => setDoomed(row),
                    },
                  ]}
                />
              </>
            )
          }
          emptyState={
            filtered ? (
              <EmptyState
                icon={IconSearchX}
                title={t('common.noResults', { term: deferred })}
                text={t('common.noResultsText')}
                action={<Button onClick={() => setTerm('')}>{t('common.clearFilters')}</Button>}
              />
            ) : (
              <EmptyState
                icon={IconInbox}
                title={empty}
                text={emptyText}
                action={
                  blocked ? undefined : (
                    <Button variant="primary" icon={IconPlus} onClick={() => setDraft({ id: null, values: blank })}>
                      {t('common.add')}
                    </Button>
                  )
                }
              />
            )
          }
        />

        {matching.length > 25 ? (
          <TableFooter
            total={tn('common.total', matching.length)}
            page={page}
            pageSize={pageSize}
            rowCount={matching.length}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        ) : null}
      </Card>

      <ConfirmDialog
        open={doomed !== null}
        title={doomed ? t('common.deleteTitle', { name: describe(doomed).name }) : ''}
        confirmLabel={t('common.delete')}
        onConfirm={() => (doomed ? drop(doomed) : Promise.resolve())}
        onClose={() => setDoomed(null)}
      >
        {doomed ? (
          <>
            {describe(doomed).warning ? (
              <p className="nv-row">
                <IconTriangleAlert size={16} />
                {describe(doomed).warning}
              </p>
            ) : null}
            <p>{t('common.deleteWarning')}</p>
          </>
        ) : null}
      </ConfirmDialog>
    </>
  );
}

/** The yes/no a dozen definition rows carry. Written once so it reads the same everywhere. */
export function ActiveChip({ active }: { active: boolean }) {
  const t = useTranslate();

  return (
    <Badge tone={active ? 'comment' : 'neutral'}>
      {active ? t('common.active') : t('common.inactive')}
    </Badge>
  );
}

/** A count of what depends on a row — what makes its delete button refusable. */
export function CountChip({ count, label }: { count: number; label: string }) {
  const t = useTranslate();

  return count > 0 ? (
    <Badge tone="scale" shape="square">{`${count} ${label}`}</Badge>
  ) : (
    <span className="nv-subtle">{t('common.none')}</span>
  );
}
